import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ListTabIndent } from './listTabIndent';

/**
 * Which formatting an {@link InlineRichText} offers. Each preset pairs the
 * TipTap extensions (what the editor can hold) with the toolbar buttons (what
 * the admin can apply), so the two can't drift apart.
 */
export type InlineRichTextPreset = 'textSection' | 'article' | 'faq';

export type InlineRichTextButton =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'subscript'
  | 'superscript'
  | 'bulletList'
  | 'orderedList'
  | 'h2'
  | 'h3'
  | 'link';

// StarterKit bundles Underline (with its Ctrl+U shortcut); presets that offer
// underline add it explicitly, so the article editor can't apply it unseen.
const starterKit = StarterKit.configure({
  heading: { levels: [2, 3] },
  underline: false,
  strike: false,
  code: false,
  codeBlock: false,
  blockquote: false,
  horizontalRule: false,
  link: false,
});

export const INLINE_RICH_TEXT_PRESETS: Record<
  InlineRichTextPreset,
  { extensions: Extensions; buttons: InlineRichTextButton[] }
> = {
  // The CMS Text Section: bold, italic, underline, sub/superscript, lists, H2, H3.
  textSection: {
    extensions: [starterKit, Underline, Subscript, Superscript],
    buttons: [
      'bold',
      'italic',
      'underline',
      'subscript',
      'superscript',
      'bulletList',
      'orderedList',
      'h2',
      'h3',
    ],
  },
  // App-only News articles: only what the backend's article allow-list keeps
  // (no sub/superscript or links). Nested lists are kept, so Tab indents
  // list items.
  article: {
    extensions: [starterKit, Underline, ListTabIndent],
    buttons: ['bold', 'italic', 'underline', 'bulletList', 'orderedList', 'h2', 'h3'],
  },
  faq: {
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        underline: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, isAllowedUri: (url) => /^https:\/\//i.test(url) },
      }),
      Underline,
      Image.configure({ inline: true, allowBase64: false }),
    ],
    buttons: ['bold', 'italic', 'underline', 'bulletList', 'orderedList', 'h2', 'h3', 'link'],
  },
};
